import type { Navigate } from '../App'
import { useSettings } from '../state/store'
import { hasGrammar } from '../data/grammar'
import { languageOf } from '../lib/languages'
import { levelName, LEVEL_TIERS, scaleStep, stepLabel } from '../lib/levels'

/**
 * How the thing works, written down.
 *
 * Every screen here carries a line of its own explaining itself, which is
 * enough when you already know what you are looking at and nothing at all when
 * you do not. Two questions had no answer anywhere in the product: what does
 * this setting actually change, and what am I supposed to do with a drilled
 * island once I have one.
 *
 * Written for the language the learner is currently in, so the examples are in
 * their script and the level is on their scale — a page that explains HSK to
 * somebody learning Italian is a page they stop reading.
 */
export function HelpView({ navigate }: { navigate: Navigate }) {
  const settings = useSettings()
  const lang = languageOf(settings.activeLanguage)
  const scale = levelName(lang.code)

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Help</div>
        <h1 className="page-title">
          <span className="page-title-zh">{lang.ui.help}</span> How this works
        </h1>
        <p className="lede">
          The short version: build a few islands, drill them until they come out without assembly,
          then put yourself under enough pressure that you find out whether they really do.
        </p>
      </header>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          The method
        </div>
        <p className="english" style={{ marginTop: 0 }}>
          A <strong>language island</strong> is Boris Shekhtman's term for a small set of lines on
          one topic, drilled until you can deliver them without thinking. Not a vocabulary list and
          not a phrasebook — the handful of things you actually need to say about a subject, in the
          order a real exchange takes them.
        </p>
        <p className="english">
          The point is not to know them. It is to be able to produce them under pressure, in the
          two seconds you get before a conversation moves on without you. Five islands you can
          deliver cold beat fifty you can recognise.
        </p>
        <p className="english" style={{ marginBottom: 0 }}>
          The starter islands are deliberately wrong about you in the details. Open one, press{' '}
          <strong>Edit</strong>, and rewrite it into your own life — your job, your family, your
          reasons. An island about somebody else is one you will not drill.
        </p>
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          A week that works
        </div>
        <ol className="english" style={{ marginTop: 0, paddingLeft: '1.1rem' }}>
          <li>
            <strong>Set your level</strong> in the rail — the {scale} row under the speech slider.
            It decides which islands you are shown and what Claude aims at when it drafts one.
          </li>
          <li>
            <strong>Rewrite two or three islands</strong> so they are true about you. Do not do all
            of them; you will not drill twelve.
          </li>
          <li>
            <strong>Listen</strong> to each one a few times, then <strong>Recall</strong> until you
            can type it back without looking.
          </li>
          <li>
            <strong>Perform</strong> against the clock. This is where you find out whether you have
            it, because there is nothing on screen but the English.
          </li>
          <li>
            <strong>Ambush</strong> yourself once a day. It fires a block at you from any island you
            have, with no warning about which — the closest thing here to being asked a question.
          </li>
          <li>
            <strong>Come back to Today.</strong> It tells you what is due. Clearing a lapsed island
            costs more than maintaining a fresh one.
          </li>
        </ol>
        <p className="muted" style={{ fontSize: '0.86rem', marginBottom: 0 }}>
          Ten minutes a day beats an hour on Sunday. The schedule is built on that assumption.
        </p>
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          The screens
        </div>
        <dl className="english" style={{ margin: 0 }}>
          <dt>
            <strong>Today</strong>
          </dt>
          <dd style={{ margin: '0 0 0.7rem' }}>
            What is due, your streak, and — once you have drilled enough — which tone or form keeps
            going missing across everything you have done.
          </dd>
          <dt>
            <strong>Islands</strong>
          </dt>
          <dd style={{ margin: '0 0 0.7rem' }}>
            Everything at or below your level. Anything harder sits behind a button at the bottom
            with the count on it; nothing is ever deleted or locked. Build your own here, by hand or
            with Claude.
          </dd>
          <dt>
            <strong>Scenarios</strong>
          </dt>
          <dd style={{ margin: '0 0 0.7rem' }}>
            A staged conversation. Each beat is the other person speaking plus a cue telling you
            what you now have to do, and every beat is answerable from an island you already have.
            This is the rehearsal for deploying an island at the right moment rather than reciting
            it.
          </dd>
          <dt>
            <strong>Ambush</strong>
          </dt>
          <dd style={{ margin: '0 0 0.7rem' }}>
            One block, no warning, a clock. The islands it draws from are the ones at your level.
          </dd>
          {lang.code === 'zh' && (
            <>
              <dt>
                <strong>Refresher</strong>
              </dt>
              <dd style={{ margin: '0 0 0.7rem' }}>
                Phrases built from the hundred most frequent characters, with those characters
                marked. Not a drill — somewhere to keep the commonest material warm.
              </dd>
            </>
          )}
          {hasGrammar(lang.code) && (
            <>
              <dt>
                <strong>Grammar</strong>
              </dt>
              <dd style={{ margin: '0 0 0.7rem' }}>
                The rules behind the islands, stated plainly. Each has a formula — subject, time,
                place, verb — and its examples are split into the pieces the formula names, with the
                mistake English speakers actually make shown beside the fix. It follows your level
                like the islands do, with harder rules behind a button, and nothing on it is
                drilled: when a rule clicks, go and find it in your islands.
              </dd>
            </>
          )}
          <dt>
            <strong>The readiness rail</strong>
          </dt>
          <dd style={{ margin: 0 }}>
            Down the left, every island at your level with a badge: <em>cold</em> means never
            drilled or long lapsed, <em>warming</em> means in progress, <em>ready</em> means you
            scored 80 or better last time, <em>automatic</em> means 90 or better. Automatic is the
            one that matters — it is the point where you stop assembling the sentence.
          </dd>
        </dl>
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Every setting, and what it actually changes
        </div>

        <dl className="english" style={{ margin: 0 }}>
          <dt>
            <strong>Your level</strong> — rail, and Settings
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            Six steps, {stepLabel(lang.code, 1)} to {stepLabel(lang.code, 6)}. It does three things:
            decides which islands the app puts in front of you, decides what is counted as due, and
            tells the AI what to pitch new content at. It hides nothing — harder islands move behind
            a button and stay openable — and it is kept <strong>per language</strong>, so answering
            honestly for a language you have just started does not disturb one you have been at for
            a year. You are currently on {scaleStep(lang.code, LEVEL_TIERS[0].level)} or above; move
            it whenever the material stops fitting.
          </dd>

          <dt>
            <strong>Speaking voice</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            Which of your device's voices reads {lang.name}. Kept per language, because one voice
            cannot serve five. If the list is short or the accent is wrong, that is your operating
            system's voice set, not the app — install more voices in your system settings and they
            appear here.
          </dd>

          <dt>
            <strong>Speed</strong> and <strong>Slower</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            Speed is the pace everything is read at, and the slider in the rail changes the same
            setting from any screen. Slower is what the <em>Slower</em> button beside each line
            uses. Below about 0.4× most voices slur rather than separate words — usually a fault,
            occasionally what you want when you are shadowing and need room between syllables.
          </dd>

          {lang.modes.length > 1 && (
            <>
              <dt>
                <strong>Default typing mode</strong>
              </dt>
              <dd style={{ margin: '0 0 0.8rem' }}>
                Whether the drills expect {lang.targetLabel} or {lang.phoneticLabel}. Typing
                characters with an IME is the harder and more useful drill; phonetics is the one to
                fall back to when you are on a keyboard that cannot produce them.
              </dd>
            </>
          )}

          {lang.hasPhonetics && (
            <>
              <dt>
                <strong>Require tones</strong>
              </dt>
              <dd style={{ margin: '0 0 0.8rem' }}>
                On, a tone slip costs you half the mark. Off, it costs nothing — but the ribbon
                still shows it, so you can see the error without being punished for it while you
                are learning a new island. Turn it back on before you decide you know one.
              </dd>
            </>
          )}

          <dt>
            <strong>Seconds per block</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            How long Perform gives you per line. The default is deliberately tight. If you are
            missing lines you know, raise it; if you are finishing with time to spare, lower it —
            the discomfort is the exercise.
          </dd>

          <dt>
            <strong>Ambush seconds</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            The same, for an ambush. Shorter than Perform on purpose: an ambush is one block and
            real questions do not wait.
          </dd>

          <dt>
            <strong>Show phonetics / Show English in Listen</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            What is on screen while a line is read to you. Both on to begin with; turn them off as
            an island firms up, so listening becomes comprehension rather than reading along.
          </dd>

          <dt>
            <strong>Daily reminder</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            On the Android app only, for now. It sends one notification a day at a time you pick.
            The web app cannot do this yet.
          </dd>

          <dt>
            <strong>Appearance</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            Dark or light, and <em>Reduce motion</em>, which removes the transitions if they bother
            you or your system already asks for less movement.
          </dd>

          <dt>
            <strong>Your own AI key</strong>
          </dt>
          <dd style={{ margin: '0 0 0.8rem' }}>
            <em>Build with AI</em> writes new islands and scenarios for you, and it runs on this
            site's key with a cap of twenty a day — a limit that exists because the calls cost us
            money, not because twenty is the right number for you. Add a key of your own and the
            cap becomes whatever your account with that provider allows.
            <br />
            <br />
            Any one of <strong>Anthropic</strong>, <strong>OpenAI</strong> or{' '}
            <strong>Google Gemini</strong> works, and there is a link beside each field to the page
            where you get one. You only need one; if you add several, the first working key in the
            order shown is the one that pays, and the screen says which that is. A key is checked
            with its provider the moment you save it, so a typo or an empty account is something you
            find out here rather than the next time you press Generate.
            <br />
            <br />
            It is stored encrypted and never shown to you again — only its last four characters —
            and there is no way to read one back out. Being straight about the limit of that: this
            site holds the key that unseals yours, so treat it as protected from a database leak
            rather than from us. Remove it whenever you like, and everything falls back to the
            site's key and the daily cap.
          </dd>

          <dt>
            <strong>Export, import, and restore</strong>
          </dt>
          <dd style={{ margin: 0 }}>
            Export writes everything — islands, scenarios, history — to a file you keep. Import
            replaces what is on this device with a file. <em>Restore missing starter content</em>{' '}
            brings back starter islands you deleted, and leaves anything you edited alone.
          </dd>
        </dl>
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Your account, and where your work lives
        </div>
        <p className="english" style={{ marginTop: 0 }}>
          Your islands live on <strong>this device</strong> first. The app works with no account and
          no connection; signing in adds sync, so the same islands and the same practice history are
          on your phone and your laptop together.
        </p>
        <p className="english">
          Each language is a separate subscription, and each has its own site — the address bar
          decides which language you are in, so a bookmark to one always opens that one.
        </p>
        <p className="english" style={{ marginBottom: 0 }}>
          Deleting an island deletes it everywhere on your next sync. Export first if you are not
          sure.
        </p>
      </div>

      <div className="row" style={{ marginTop: '1.4rem' }}>
        <button type="button" className="btn is-primary" onClick={() => navigate({ view: 'islands' })}>
          Go to your islands
        </button>
        <button type="button" className="btn" onClick={() => navigate({ view: 'settings' })}>
          Open Settings
        </button>
      </div>
    </>
  )
}
